import { IconButton } from "@chakra-ui/button";
import { DeleteIcon, RepeatClockIcon } from "@chakra-ui/icons";
import { Box, HStack } from "@chakra-ui/layout";
import { SkeletonText } from "@chakra-ui/skeleton";
import { Table, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/table";
import { Tag } from "@chakra-ui/tag";
import React from "react";
import { memo } from "react";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import DateUtil from "../../util/DateUtil";

interface MemberSelfServiceConfigurationTableProps {
  configurations:
    | CherryHubAdminApi.MemberSelfServiceConfigurationSummaryItem[]
    | null;
  isLoading: boolean;
  debouncedQuery: string;
  disabledActions?: boolean;
  onClickEdit: (
    configuration: CherryHubAdminApi.MemberSelfServiceConfigurationSummaryItem
  ) => void;
  onClickDelete: (
    configuration: CherryHubAdminApi.MemberSelfServiceConfigurationSummaryItem
  ) => void;
  onClickRestore: (
    configuration: CherryHubAdminApi.MemberSelfServiceConfigurationSummaryItem
  ) => void;
}

export const MemberSelfServiceConfigurationTable = memo(
  ({
    configurations,
    isLoading,
    debouncedQuery,
    disabledActions,
    onClickEdit,
    onClickDelete,
    onClickRestore,
  }: MemberSelfServiceConfigurationTableProps) => {
    return (
      <Box w="100%" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th w="0.1%" whiteSpace="nowrap">
                Config #
              </Th>
              <Th>Name</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th>Last Modified</Th>
              <Th>&nbsp;</Th>
            </Tr>
          </Thead>
          <Tbody>
            {!isLoading &&
              (configurations ?? []).map((row) => (
                <Tr key={row.Meta?.Id}>
                  <Td textAlign="center" w="0.1%" whiteSpace="nowrap">
                    {row.MemberSelfServiceConfigurationNo}
                  </Td>
                  <Td
                    title="View member self-service configuration"
                    _hover={
                      !row.Meta?.IsDeleted
                        ? { textDecoration: "underline", cursor: "pointer" }
                        : {}
                    }
                    onClick={() => !row.Meta?.IsDeleted && onClickEdit(row)}
                  >
                    {!!row.Meta?.IsDeleted && (
                      <Tag size="sm" mr="2">
                        Deleted
                      </Tag>
                    )}
                    {row.DisplayName}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <Tag
                      size="sm"
                      bgColor={row.IsEnabled ? "" : "cherryButton.500"}
                      color={row.IsEnabled ? "" : "white"}
                    >
                      {row.IsEnabled ? "Enabled" : "Disabled"}
                    </Tag>
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    {DateUtil.getLocalDateFormatFromString(
                      row.Meta?.DateCreated
                    )}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    {DateUtil.getLocalDateFormatFromString(
                      row.Meta?.DateUpdated
                    )}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <HStack spacing={2} justifyContent="center">
                      {(row.Meta?.IsDeleted && (
                        <IconButton
                          aria-label="Restore member self-service configuration."
                          title="Restore member self-service configuration."
                          icon={<RepeatClockIcon />}
                          size="xs"
                          isDisabled={disabledActions}
                          onClick={() => onClickRestore(row)}
                        />
                      )) || (
                        <IconButton
                          marginLeft="1"
                          aria-label="Delete member self-service configuration."
                          title="Delete member self-service configuration."
                          icon={<DeleteIcon />}
                          size="xs"
                          isDisabled={disabledActions}
                          onClick={() => onClickDelete(row)}
                        />
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            {configurations?.length === 0 && !isLoading && (
              <Tr>
                <Td colSpan={6} textAlign="center">
                  No member self-service configuration found matching query '
                  {debouncedQuery}'
                </Td>
              </Tr>
            )}
            {isLoading && (
              <Tr>
                <Td width="5%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="30%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="5%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="10%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="10%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="5%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    );
  }
);
